/**
 * TeamSystem Impact - About Page
 * Mostra versione, build info e changelog della release corrente
 */

import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  Accessibility as A11yIcon,
  CheckCircleOutline as CheckIcon,
  InfoOutlined as InfoIcon,
  NewReleases as ReleaseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export const AboutPage = () => {
  const { t, i18n } = useTranslation();

  const changelogKeys = [
    'stakeholderAssessment',
    'scoringAlgorithm',
    'materialityDashboard',
    'catalogViews',
    'exportImport',
    'financialValidation',
    'obsoleteCleanup',
    'documentation',
  ] as const;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Version Info */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <InfoIcon color="primary" />
            <Typography variant="h6" component="h1">{t('about.version')}</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('about.version')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '2rem' }}>
                {__APP_VERSION__}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('about.buildDate')}
              </Typography>
              <Typography variant="body1">
                {new Date(__BUILD_DATE__).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'it-IT', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('about.commit')}
              </Typography>
              <Chip
                label={__GIT_COMMIT__}
                size="small"
                variant="outlined"
                sx={{ fontFamily: 'monospace' }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* WCAG Accessibility Compliance */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <A11yIcon color="primary" />
            <Typography variant="h6" component="h2">
              {t('about.accessibility', 'Accessibilità')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('about.wcagLevel', 'Livello WCAG')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '2rem' }}>
                  2.1
                </Typography>
                <Chip
                  label={t('about.wcagBadge')}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('about.wcagDescription', 'La piattaforma è conforme agli standard WCAG 2.1 Livello AA. Sono implementati: contrasto colori 4.5:1, contrasto non-testo 3:1, navigazione da tastiera, indicatori di focus visibili, gerarchia heading semantica, attributi ARIA su tutti i componenti interattivi.')}
          </Typography>

          <List dense>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('about.a11yLanguage', 'Lingua della pagina dichiarata (lang="it")')}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('about.a11yStructure', 'Struttura semantica HTML con componenti MUI accessibili')}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('about.a11yAria', 'Attributi ARIA su componenti principali')}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('about.a11yKeyboard', 'Navigazione da tastiera completa con supporto Tab, Enter, Space')}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('about.a11yFocus', 'Indicatori di focus visibili su tutti i componenti interattivi')}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('about.a11yContrast', 'Contrasto colori testo 4.5:1 e non-testo 3:1')}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('about.a11yHeadings', 'Gerarchia heading semantica (h1 → h2 → h3)')}
              />
            </ListItem>
          </List>

          <Typography variant="caption" color="text.secondary">
            {t('about.a11yLastAudit', 'Ultimo audit: 8 marzo 2026 — Conforme WCAG 2.1 Livello AA')}
          </Typography>
        </CardContent>
      </Card>

      {/* Changelog */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ReleaseIcon color="primary" />
            <Typography variant="h6" component="h2">{t('about.changelog')}</Typography>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('about.releaseTitle')} — v{__APP_VERSION__}
          </Typography>

          <List dense>
            {changelogKeys.map((key) => (
              <ListItem key={key} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t(`about.changelogItems.${key}`)} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};
